import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontalIcon, PlusIcon, SparklesIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserMenu } from "@/features/auth/UserMenu";
import {
  roadmapsQuery,
  useCreateDemoRoadmap,
  useCreateRoadmap,
  useDeleteRoadmap,
  useRoadmaps,
} from "@/features/roadmap/queries";
import { ApiError } from "@/lib/api";

export const Route = createFileRoute("/_authed/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(roadmapsQuery()),
  component: RoadmapListPage,
});

function RoadmapListPage() {
  const { data: roadmaps = [] } = useRoadmaps();
  const navigate = useNavigate();
  const createDemo = useCreateDemoRoadmap();
  const deleteRoadmap = useDeleteRoadmap();
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const loadDemo = () =>
    createDemo.mutate(undefined, {
      onSuccess: (rm) => navigate({ to: "/r/$roadmapId", params: { roadmapId: rm.id } }),
      onError: (e) => toast.error(e instanceof ApiError ? e.message : "Could not create demo"),
    });

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col gap-8 overflow-y-auto px-6 py-10">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Roadmaps</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Timelines you can shape in seconds and present with pride.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setCreateOpen(true)} data-testid="new-roadmap">
            <PlusIcon data-icon="inline-start" />
            New roadmap
          </Button>
          <UserMenu />
        </div>
      </header>

      {roadmaps.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed py-20 text-center"
          data-testid="roadmap-list"
        >
          <p className="text-base font-medium">No roadmaps yet</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Create an empty roadmap, or load the demo to see items, phases and zoom levels in
            action.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="outline"
              onClick={loadDemo}
              disabled={createDemo.isPending}
              data-testid="load-demo-empty"
            >
              <SparklesIcon data-icon="inline-start" />
              Load demo
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <PlusIcon data-icon="inline-start" />
              New roadmap
            </Button>
          </div>
        </div>
      ) : (
        <ul
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="roadmap-list"
        >
          {roadmaps.map((rm) => (
            <li
              key={rm.id}
              className="group relative rounded-xl border bg-card transition hover:-translate-y-px hover:border-foreground/30 hover:shadow-sm"
              data-testid={`roadmap-card-${rm.id}`}
            >
              <Link
                to="/r/$roadmapId"
                params={{ roadmapId: rm.id }}
                className="block rounded-xl p-5 pr-12 outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <h2 className="truncate text-base font-medium">{rm.name}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {rm.itemCount} {rm.itemCount === 1 ? "item" : "items"} · updated{" "}
                  {formatDistanceToNow(new Date(rm.updatedAt), { addSuffix: true })}
                </p>
                <div className="mt-4 flex h-1.5 gap-1" aria-hidden>
                  {(rm.colors.length ? rm.colors : ["var(--border)"]).map((c, i) => (
                    <span key={i} className="flex-1 rounded-full" style={{ backgroundColor: c }} />
                  ))}
                </div>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="absolute top-3 right-3 opacity-60 group-hover:opacity-100"
                    aria-label="Roadmap actions"
                    data-testid="roadmap-card-menu"
                  >
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setPendingDelete({ id: rm.id, name: rm.name })}
                  >
                    <Trash2Icon />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          ))}
        </ul>
      )}

      <CreateRoadmapDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onLoadDemo={loadDemo}
        demoPending={createDemo.isPending}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the roadmap with all its items and phases. There is no undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (pendingDelete) deleteRoadmap.mutate(pendingDelete.id);
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateRoadmapDialog({
  open,
  onOpenChange,
  onLoadDemo,
  demoPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoadDemo: () => void;
  demoPending: boolean;
}) {
  const [name, setName] = useState("");
  const navigate = useNavigate();
  const create = useCreateRoadmap();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      { name: trimmed },
      {
        onSuccess: (rm) => {
          onOpenChange(false);
          setName("");
          navigate({ to: "/r/$roadmapId", params: { roadmapId: rm.id } });
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : "Could not create"),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="contents">
          <DialogHeader>
            <DialogTitle>New roadmap</DialogTitle>
            <DialogDescription>You can rename it at any time from the editor.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="roadmap-name">Name</Label>
            <Input
              id="roadmap-name"
              autoFocus
              placeholder="Product roadmap 2027"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="roadmap-name-input"
            />
          </div>
          <DialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground"
              onClick={onLoadDemo}
              disabled={demoPending}
              data-testid="load-demo"
            >
              <SparklesIcon data-icon="inline-start" />
              Load the demo instead
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!name.trim() || create.isPending}
                data-testid="roadmap-create-submit"
              >
                Create
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
